// This file is auto-generated by @hey-api/openapi-ts

import { type Options as ClientOptions, type TDataShape, type Client, urlSearchParamsBodySerializer } from '@hey-api/client-axios';
import type { GetLoggedInCustomerData, GetLoggedInCustomerResponse, GetLoggedInCustomerError, GetContactDetailsWithKurtIdFromHeaderData, GetContactDetailsWithKurtIdFromHeaderResponse, GetContactDetailsWithKurtIdFromHeaderError, UpdateContactDetailsWithKurtFromHeaderData, UpdateContactDetailsWithKurtFromHeaderError, ValidateEmailCodeAndUpdateEmailAddressData, ValidateEmailCodeAndUpdateEmailAddressError, SendEmailVerificationCodeData, SendEmailVerificationCodeError, SendSmsVerificationCodeData, SendSmsVerificationCodeError, ValidateSmsCodeAndUpdatePhoneNumberData, ValidateSmsCodeAndUpdatePhoneNumberError, GetCustomerByNameAndBirthdateAndPostalCodeData, GetCustomerByNameAndBirthdateAndPostalCodeResponse, GetCustomerByNameAndBirthdateAndPostalCodeError, GetCustomerByUserIdData, GetCustomerByUserIdResponse, GetCustomerByUserIdError } from './types.gen';
import { client as _heyApiClient } from './client.gen';
import type { CacheOptions } from 'axios-cache-interceptor';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean> = ClientOptions<TData, ThrowOnError> & {
    cache: CacheOptions;
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: Record<string, unknown>;
};

/**
 * Get information about the logged in customer
 */
export const getLoggedInCustomer = <ThrowOnError extends boolean = false>(options?: Options<GetLoggedInCustomerData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetLoggedInCustomerResponse, GetLoggedInCustomerError, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/customers/v1/',
        ...options
    });
};

/**
 * Get the contact details for the logged in SSO user
 */
export const getContactDetailsWithKurtIdFromHeader = <ThrowOnError extends boolean = false>(options?: Options<GetContactDetailsWithKurtIdFromHeaderData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetContactDetailsWithKurtIdFromHeaderResponse, GetContactDetailsWithKurtIdFromHeaderError, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/customers/v1/contactdetails',
        ...options
    });
};

/**
 * Update the contact information for the logged in user
 * None of the contact information parameters are required, but one of them must be set for it to be updated
 */
export const updateContactDetailsWithKurtFromHeader = <ThrowOnError extends boolean = false>(options?: Options<UpdateContactDetailsWithKurtFromHeaderData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).put<unknown, UpdateContactDetailsWithKurtFromHeaderError, ThrowOnError>({
        ...urlSearchParamsBodySerializer,
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/customers/v1/contactdetails',
        ...options,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...options?.headers
        }
    });
};

/**
 * Verify that the code presented by the customer is the same code as sent to the customer, and update the customers contact email address if the code was correct.
 */
export const validateEmailCodeAndUpdateEmailAddress = <ThrowOnError extends boolean = false>(options: Options<ValidateEmailCodeAndUpdateEmailAddressData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).put<unknown, ValidateEmailCodeAndUpdateEmailAddressError, ThrowOnError>({
        ...urlSearchParamsBodySerializer,
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/customers/v1/contactdetails/verification/email',
        ...options,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...options?.headers
        }
    });
};

/**
 * Send code to customers email, customer must present this code to validate the email address.
 */
export const sendEmailVerificationCode = <ThrowOnError extends boolean = false>(options: Options<SendEmailVerificationCodeData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).post<unknown, SendEmailVerificationCodeError, ThrowOnError>({
        ...urlSearchParamsBodySerializer,
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/customers/v1/contactdetails/verification/send/email',
        ...options,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...options?.headers
        }
    });
};

/**
 * Send code to customers phone number, customer must present this code to validate the phone number.
 */
export const sendSmsVerificationCode = <ThrowOnError extends boolean = false>(options: Options<SendSmsVerificationCodeData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).post<unknown, SendSmsVerificationCodeError, ThrowOnError>({
        ...urlSearchParamsBodySerializer,
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/customers/v1/contactdetails/verification/send/sms',
        ...options,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...options?.headers
        }
    });
};

/**
 * Verify that the code presented by the customer is the same code as sent to the customer, and update the customers contact phone number if the code was correct.
 */
export const validateSmsCodeAndUpdatePhoneNumber = <ThrowOnError extends boolean = false>(options: Options<ValidateSmsCodeAndUpdatePhoneNumberData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).put<unknown, ValidateSmsCodeAndUpdatePhoneNumberError, ThrowOnError>({
        ...urlSearchParamsBodySerializer,
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/customers/v1/contactdetails/verification/sms',
        ...options,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...options?.headers
        }
    });
};

/**
 * Get information about a user based on name + (birth date and postal code)
 */
export const getCustomerByNameAndBirthdateAndPostalCode = <ThrowOnError extends boolean = false>(options: Options<GetCustomerByNameAndBirthdateAndPostalCodeData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetCustomerByNameAndBirthdateAndPostalCodeResponse, GetCustomerByNameAndBirthdateAndPostalCodeError, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/customers/v1/search',
        ...options
    });
};

/**
 * Get information about a user based on userId as fetched from response from another resource.
 * The userId must be URL encoded before used as path parameter
 */
export const getCustomerByUserId = <ThrowOnError extends boolean = false>(options: Options<GetCustomerByUserIdData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetCustomerByUserIdResponse, GetCustomerByUserIdError, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/customers/v1/userids/{userId}',
        ...options
    });
};